import { useLazyQuery } from "@apollo/client";
import { ExternalMatterData, ExternalMatterLazyQueryInput } from "@design-system";
import { QueryMode } from "~/gql/graphql";
import { FILTER_EXTERNAL_MATTERS } from "../../graphql/query";

const buildExternalMattersFilter = ({
  clientId,
  statuses,
  search,
  leadFeeEarnerId,
}: ExternalMatterLazyQueryInput) => ({
  AND: [
    { closed: { equals: false } },
    { deprecated: { equals: false } },
    { status: { in: statuses } },
    ...(clientId ? [{ clientId: { equals: clientId } }] : []),
    ...(search
      ? [
          {
            OR: [
              { title: { contains: search, mode: QueryMode.Insensitive } },
              { externalReference: { contains: search, mode: QueryMode.Insensitive } },
              { client: { is: { name: { contains: search, mode: QueryMode.Insensitive } } } },
            ],
          },
        ]
      : []),
    ...(leadFeeEarnerId ? [{ leadFeeEarnerId: { equals: leadFeeEarnerId } }] : []),
  ],
});

export const useExternalMattersQueryLazy = (): ExternalMatterData => {
  const [
    getExternalMattersRaw,
    { called, fetchMore: fetchMoreRaw, data: externalMatterData, loading, refetch: refetchRaw },
  ] = useLazyQuery(FILTER_EXTERNAL_MATTERS);

  const externalMatters = externalMatterData?.filteredExternalMatters?.externalMatters || [];
  const total = externalMatterData?.filteredExternalMatters?.total || 0;

  return {
    loading,
    called,
    fetchMore: (queryInput: ExternalMatterLazyQueryInput) => {
      fetchMoreRaw &&
        fetchMoreRaw({
          variables: {
            input: {
              filter: buildExternalMattersFilter(queryInput),
              offset: externalMatters.length ?? 0,
              limit: 10,
            },
          },
          updateQuery(prev, { fetchMoreResult }) {
            if (!fetchMoreResult) return prev;
            return {
              ...prev,
              filteredExternalMatters: {
                ...prev.filteredExternalMatters,
                externalMatters: [
                  ...prev.filteredExternalMatters.externalMatters,
                  ...fetchMoreResult.filteredExternalMatters.externalMatters,
                ],
                total: fetchMoreResult.filteredExternalMatters.total,
              },
            };
          },
        });
    },
    get: ({
      clientId,
      statuses,
      search,
      limit = 10,
      offset = 0,
      leadFeeEarnerId,
    }: ExternalMatterLazyQueryInput) => {
      getExternalMattersRaw({
        variables: {
          input: {
            filter: buildExternalMattersFilter({ clientId, statuses, search, leadFeeEarnerId }),
            offset,
            limit,
          },
        },
      });
    },
    refetch: ({
      clientId,
      statuses,
      search,
      limit = 10,
      offset = 0,
      leadFeeEarnerId,
    }: ExternalMatterLazyQueryInput) => {
      refetchRaw({
        input: {
          filter: buildExternalMattersFilter({ clientId, statuses, search, leadFeeEarnerId }),
          offset,
          limit,
        },
      });
    },
    data: {
      items: externalMatters as any[],
      total,
    },
  };
};
